import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, Calculator, Zap } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BusinessProcessTransformationCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './BusinessProcessTransformationCostCalculatorPage/components/ResultsOverview';
import DetailedBreakdown from './BusinessProcessTransformationCostCalculatorPage/components/DetailedBreakdown';
import QuickAdjustmentSidebar from './BusinessProcessTransformationCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './BusinessProcessTransformationCostCalculatorPage/components/ComparisonSidebar';
import ResourcesSidebar from './BusinessProcessTransformationCostCalculatorPage/components/ResourcesSidebar';
import InfoContent from './BusinessProcessTransformationCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры
  companySize: 'medium', // small, medium, large, enterprise
  industryType: 'manufacturing', // manufacturing, retail, healthcare, finance, technology, other
  transformationType: 'comprehensive', // comprehensive, digital, lean, agile, automation
  
  // Продолжительность проекта
  projectDuration: '12', // в месяцах
  
  // Персонал и ресурсы
  internalTeamSize: '5', // количество сотрудников
  averageSalary: '60000', // среднемесячная зарплата в рублях
  allocatedTimePercentage: '30', // % времени, выделяемого на проект
  
  // Консалтинг
  consultingRequired: true,
  consultingLevel: 'standard', // basic, standard, premium
  consultingDuration: '6', // месяцев
  
  // Технологические аспекты
  softwareImplementation: true,
  softwareType: 'custom', // custom, off-the-shelf, hybrid
  softwareCost: '2000000', // рубли
  hardwareUpgrades: false,
  hardwareCost: '500000', // рубли
  
  // Тренинги и развитие
  trainingRequired: true,
  trainingIntensity: 'medium', // low, medium, high
  employeesToTrain: '20', // количество сотрудников
  trainingCostPerEmployee: '30000', // рубли
  
  // Другие факторы
  changeSeverity: 'moderate', // minor, moderate, significant, radical
  organizationalReadiness: 'prepared', // unprepared, somewhat-prepared, prepared, fully-prepared
  includeContingency: true,
  contingencyPercentage: '15', // процент от общей стоимости
  
  // ROI и выгоды
  includeROICalculation: true,
  expectedEfficiencyGain: '20', // процент
  expectedRevenueIncrease: '10', // процент
  currentAnnualRevenue: '50000000', // рубли
  currentOperationalCosts: '30000000', // рубли
};

// Типы трансформации
export const transformationTypes = [
  { id: 'comprehensive', name: 'Комплексная трансформация', description: 'Полномасштабная перестройка ключевых бизнес-процессов' },
  { id: 'digital', name: 'Цифровая трансформация', description: 'Внедрение цифровых технологий и инструментов' },
  { id: 'lean', name: 'Бережливое производство', description: 'Оптимизация и устранение потерь в процессах' },
  { id: 'agile', name: 'Agile-трансформация', description: 'Переход к гибким методологиям управления' },
  { id: 'automation', name: 'Автоматизация процессов', description: 'Внедрение автоматизированных систем и роботизации' }
];

// Типы компаний по размеру
export const companySizes = [
  { id: 'small', name: 'Малый бизнес', description: 'До 50 сотрудников' },
  { id: 'medium', name: 'Средний бизнес', description: '50-250 сотрудников' },
  { id: 'large', name: 'Крупный бизнес', description: '250-1000 сотрудников' },
  { id: 'enterprise', name: 'Корпорация', description: 'Более 1000 сотрудников' }
];

// Отрасли
export const industryTypes = [
  { id: 'manufacturing', name: 'Производство', complexityFactor: 1.1 },
  { id: 'retail', name: 'Розничная торговля', complexityFactor: 0.9 },
  { id: 'healthcare', name: 'Здравоохранение', complexityFactor: 1.3 },
  { id: 'finance', name: 'Финансы и банки', complexityFactor: 1.2 },
  { id: 'technology', name: 'Технологии и IT', complexityFactor: 1.0 },
  { id: 'logistics', name: 'Логистика и транспорт', complexityFactor: 1.05 },
  { id: 'education', name: 'Образование', complexityFactor: 0.95 },
  { id: 'energy', name: 'Энергетика', complexityFactor: 1.15 },
  { id: 'construction', name: 'Строительство', complexityFactor: 1.05 },
  { id: 'other', name: 'Другие отрасли', complexityFactor: 1.0 }
];

// Уровни консалтинга
export const consultingLevels = [
  { id: 'basic', name: 'Базовый', description: 'Общие рекомендации и периодические консультации', costFactor: 0.7 },
  { id: 'standard', name: 'Стандартный', description: 'Регулярное сопровождение и поддержка трансформации', costFactor: 1.0 },
  { id: 'premium', name: 'Премиум', description: 'Полное сопровождение, специализированные эксперты и углубленная аналитика', costFactor: 1.5 }
];

/**
 * BusinessProcessTransformationCostCalculatorPage Component - Калькулятор стоимости трансформации бизнес-процессов
 */
const BusinessProcessTransformationCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('business-process-transformation-calculator') || {
    id: 'business-process-transformation-calculator',
    name: 'Калькулятор стоимости трансформации бизнес-процессов',
    metaTitle: 'Калькулятор стоимости трансформации бизнес-процессов | Gensio',
    metaDescription: 'Рассчитайте полную стоимость трансформации бизнес-процессов компании: консалтинг, внедрение технологий, обучение персонала, прогноз ROI и окупаемости.',
  }, []);

  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости трансформации бизнес-процессов | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте полную стоимость трансформации бизнес-процессов компании: консалтинг, внедрение технологий, обучение персонала, прогноз ROI и окупаемости.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedTransformationProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'digital-transformation':
          newState.transformationType = 'digital';
          break;
        case 'lean':
          newState.transformationType = 'lean';
          break;
        case 'agile':
          newState.transformationType = 'agile';
          break;
        case 'automation':
          newState.transformationType = 'automation';
          break;
        case 'enterprise':
          newState.companySize = 'enterprise';
          break;
        case 'sme':
          newState.companySize = 'medium';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setShowDetailedBreakdown(false);

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `Трансформация ${formData.transformationType} - ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      transformationType: formData.transformationType
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedTransformationProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedTransformationProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
      // Пересчитываем разбивку затрат для визуализации
      if (projectToLoad.results && projectToLoad.results.costBreakdown) {
        setCostBreakdown(projectToLoad.results.costBreakdown);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости трансформации бизнес-процессов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              costBreakdown={costBreakdown}
              setCostBreakdown={setCostBreakdown}
              transformationTypes={transformationTypes}
              companySizes={companySizes}
              industryTypes={industryTypes}
              consultingLevels={consultingLevels}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <DetailedBreakdown 
                  costBreakdown={costBreakdown} 
                  results={results}
                  showDetailedBreakdown={showDetailedBreakdown}
                  setShowDetailedBreakdown={setShowDetailedBreakdown}
                />
              </>
            )}

            {/* --- Информационный контент о трансформации бизнес-процессов --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              transformationTypes={transformationTypes}
              companySizes={companySizes}
            />

            {/* --- Панель сравнения проектов --- */}
            <ComparisonSidebar 
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
            />

            {/* --- Панель ресурсов и советы --- */}
            <ResourcesSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Cost Breakdown Table */
        .cost-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .cost-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default BusinessProcessTransformationCostCalculatorPage;